import Vue from 'vue';
import { isEqual, uniqBy, pick, keyBy, groupBy } from 'lodash-es';
import {
  STORAGE_DISK_SERVICE_STATUS_TEXT,
  STORAGE_DISK_SMART_STATUS_TEXT,
  STORAGE_DISK_STATUS_ICON,
  STORAGE_DISK_TYPE_ISCSI,
  STORAGE_DISK_TYPE_NVME,
  STORAGE_DISK_TYPE_SMR,
  STORAGE_DISK_TYPE_TEXT,
  STORAGE_DISK_HARD_STATUS_TEXT,
  STORAGE_DISK_JOURNAL_TYPE_TEXT,
  STORAGE_DISK_ROLE_TEXT
} from '@/common/constants/storageDisks.constants';
import { dashFormatter } from '@/common/utils/formatter.bool';
import i18n from '@/common/i18n';
import { DiskDetails, Storage_JournalType, DiskRole } from '@schema';
import getClusterId from '@/common/store/pinia/storageCluster/getClusterId';

function getInProgressStatus(disk) {
  return disk.being_released || disk.being_assigned;
}

export function getExtendedType(disk) {
  const isSMR = !!disk.zoned;
  const isNVME = !!disk.nvme;
  let extendedType = disk.type;
  if (isSMR) {
    extendedType = STORAGE_DISK_TYPE_SMR;
  }
  if (isNVME) {
    extendedType = STORAGE_DISK_TYPE_NVME;
  }
  return extendedType;
}

export function prepareDisk(disk, allDisks) {
  const { service_params: serviceParams } = disk;

  const isSystem = [
    DiskRole.CS_SYSTEM,
    DiskRole.MDS_SYSTEM,
    DiskRole.SYSTEM
  ].includes(disk.role);
  const isSwap = disk.role === DiskRole.SWAP;

  const isAssigned = [DiskRole.CS, DiskRole.CS_SYSTEM,
    DiskRole.MDS_SYSTEM, DiskRole.MDS,
    DiskRole.MDS_JOURNAL, DiskRole.JOURNAL].includes(disk.role);
  const isUnassigned = [DiskRole.SYSTEM, DiskRole.UNASSIGNED].includes(disk.role);
  const isInProgress = getInProgressStatus(disk);
  const isSMR = !!disk.zoned;
  const isNVME = !!disk.nvme;
  const isISCSI = disk.type === STORAGE_DISK_TYPE_ISCSI;
  const serialNumber = isISCSI ? `LUN-${disk.lun_id}` : disk.serial_number;
  const smartStatusText = STORAGE_DISK_SMART_STATUS_TEXT[disk.smart_status || DiskDetails.smart_status.NOT_SUPPORTED];
  const isChecksumming = [Storage_JournalType.EXTERNAL_CACHE, Storage_JournalType.INNER_CACHE].includes(serviceParams.journal_type);
  const isCacheEnabled = Storage_JournalType.EXTERNAL_CACHE === serviceParams.journal_type;
  const diskStatusText = STORAGE_DISK_HARD_STATUS_TEXT[disk.disk_status];
  const isCS = [DiskRole.CS_SYSTEM, DiskRole.CS].includes(disk.role);
  const isMDS = [DiskRole.MDS_JOURNAL, DiskRole.MDS, DiskRole.MDS_SYSTEM].includes(disk.role);
  const isCache = [DiskRole.JOURNAL, DiskRole.MDS_JOURNAL].includes(disk.role);
  let tierNumber;
  let tierText = i18n.t('figure dash');
  let cacheDiskName = '';
  let isCacheFailed = false;

  if (isCacheEnabled) {
    const diskWithCache = allDisks.find(d => d.id === serviceParams.journal_disk_id) || {};
    const { device, disk_status: diskStatus } = diskWithCache;
    cacheDiskName = device;
    isCacheFailed = [DiskDetails.disk_status.UNAVAIL, DiskDetails.disk_status.FAILED]
      .includes(diskStatus);
  }

  if ([DiskRole.CS, DiskRole.CS_SYSTEM].includes(disk.role)) {
    tierNumber = serviceParams?.tier;
    tierText = i18n.t('Tier {tierNum}', { tierNum: tierNumber });
  } else if ([DiskRole.JOURNAL, DiskRole.MDS_JOURNAL].includes(disk.role)) {
    // eslint-disable-next-line camelcase
    tierNumber = Number.isFinite(serviceParams?.journal_tier) ? serviceParams?.journal_tier : -1;
    tierText = tierNumber >= 0 ? i18n.t('Tier {tierNum}', { tierNum: tierNumber }) : i18n.t('Any tier');
  }

  const extendedType = getExtendedType(disk);

  const typeText = STORAGE_DISK_TYPE_TEXT[extendedType] || disk.type;

  return {
    ...disk,
    extendedType,
    isAssigned,
    isUnassigned,
    isChecksumming,
    isCacheEnabled,
    cacheText: dashFormatter(STORAGE_DISK_JOURNAL_TYPE_TEXT[serviceParams.journal_type]),
    cacheDiskName,
    isCacheFailed,
    diskStatusText,
    isInProgress,
    isSMR,
    isNVME,
    isSwap,
    isSystem,
    isCS,
    isMDS,
    isCache,
    isISCSI,
    name: disk.device,
    roleText: dashFormatter(STORAGE_DISK_ROLE_TEXT[disk.role]),
    serialNumber,
    spaceRatio: disk.space.used / disk.space.size,
    smartStatusText,
    tierNumber,
    tierText,
    typeText,
    statusText: STORAGE_DISK_SERVICE_STATUS_TEXT[disk.service_status],
    statusIcon: STORAGE_DISK_STATUS_ICON[disk.service_status],
    issues: disk.issues || [],
  };
}
function getCopyConfig(disk) {
  return pick(disk, ['device', 'tierNumber', 'role', 'type', 'isSystem', 'service_params']);
}

export default {
  name: 'nodesDisks',
  namespaced: true,
  state: {
    disks: {},
    pendingMap: {}
  },
  getters: {
    getDisks: state => nodeId => (state.disks[nodeId] || []).map(disk => prepareDisk(disk, state.disks[nodeId])),
    getDiskCount: state => nodeId => state.disks[nodeId]?.length,
    getPending: state => nodeId => state.pendingMap[nodeId] || false,
    hasAvailableTierForSMR: (state, getters) => nodeId => {
      const disks = getters.getDisks(nodeId);
      const disksWithCache = disks.filter(disk => [DiskRole.MDS_JOURNAL, DiskRole.JOURNAL].includes(disk.role));
      const storageDisks = disks.filter(disk => !disk.isSMR && [DiskRole.CS, DiskRole.CS_SYSTEM].includes(disk.role));
      const occupiedTiers = uniqBy(storageDisks, 'tierNumber').map(({ tierNumber }) => tierNumber);
      if (occupiedTiers.length === 4) {
        return false;
      }
      const hasAvailableTier = !!disksWithCache.find(disk => disk.tierNumber === -1 || !occupiedTiers.includes(disk.tierNumber));
      return hasAvailableTier;
    },
    getCacheDisks: (state, getters) => (nodeId, cacheId) => getters.getDisks(nodeId).filter(({ service_params: serviceParams }) => serviceParams && serviceParams.journal_disk_id === cacheId),
    getDiagnosticResult: state => id => state.diagnostic[id],
    getDisksCopyConfig: (state, getters) => nodeId => groupBy(getters.getDisks(nodeId).map(getCopyConfig), 'role'),
    getStorageDisksByTier: (state, getters) => nodeId => tier => getters.getDisks(nodeId)
      .filter(disk => disk.isCS && disk.tierNumber === tier)
  },
  mutations: {
    SET_DISKS(state, { nodeId, disks }) {
      let nodeDisks = state.disks[nodeId];
      if (nodeDisks) {
        // clear the list of disks if they are not in the new list
        state.disks[nodeId] = nodeDisks.filter(oldDisk => disks.find(newDisk => newDisk.id === oldDisk.id));
        nodeDisks = state.disks[nodeId];
        disks.forEach(disk => {
          const index = nodeDisks.findIndex(({ id }) => disk.id === id);
          if (index === -1) {
            nodeDisks.push(disk);
          } else if (!isEqual(disk, nodeDisks[index])) {
            nodeDisks.splice(index, 1, disk);
          }
        });
      } else {
        Vue.set(state.disks, nodeId, disks);
      }
    },
    ADD_DISK(state, disk) {
      const nodeDisks = state.disks[disk.node_id];
      if (nodeDisks) {
        const index = nodeDisks.findIndex(it => it.device === disk.device);
        if (index > -1) {
          nodeDisks.splice(index, 1, disk);
        } else {
          nodeDisks.push(disk);
        }
      } else {
        Vue.set(state.disks, disk.node_id, [disk]);
      }
    },
    DELETE_DISK(state, { node_id: nodeId, device }) {
      const nodeDisks = state.disks[nodeId];
      if (nodeDisks) {
        const index = nodeDisks.findIndex(it => it.device === device);
        if (index > -1) {
          nodeDisks.splice(index, 1);
        }
      }
    },
    PATCH_DISK(state, disk) {
      const nodeDisks = state.disks[disk.node_id];
      if (nodeDisks) {
        const index = nodeDisks.findIndex(it => it.device === disk.device);
        if (index > -1) {
          Object.assign(nodeDisks[index], disk);
        }
      }
    },
    SET_PENDING(state, { nodeId, status }) {
      Vue.set(state.pendingMap, nodeId, status);
    }
  },
  actions: {
    FETCH_DISKS({ commit }, { nodeId }) {
      commit('SET_PENDING', { nodeId, status: true });
      return this.$api.get(`/api/v2/nodes/${nodeId}/disks`)
        .then((response) => {
          commit('SET_DISKS', { nodeId, disks: response.data.data });
          return response;
        })
        .finally(() => {
          commit('SET_PENDING', { nodeId, status: false });
        });
    },
    RELEASE_DISKS(_, { nodeId, disks, force }) {
      return getClusterId()
        .then(clusterId => this.$api.post(`/api/v2/${clusterId}/nodes/${nodeId}/disks/release`, {
          disks,
          force,
          event: {
            name: 'ReleaseDisks',
            nodeId,
            disks
          }
        }));
    },
    FETCH_AVAILABLE_ROLES(_, { nodeId, disksIds }) {
      return this.$api.post(`/api/v2/nodes/${nodeId}/disks/make_disks_available_roles`, {
        disks_to_assign: disksIds
      }).then(({ data }) => keyBy(data, 'role'));
    },
    ASSIGN_DISKS(_, { nodeId, ...payload }) {
      return getClusterId()
        .then(clusterId => this.$api.post(`/api/v2/${clusterId}/nodes/${nodeId}/disks`, payload));
    },
    BLINK(_, disk) {
      return this.$api.post(`/api/v2/nodes/${disk.node_id}/disks/${disk.id}/blink`);
    },
    UNBLINK(_, disk) {
      return this.$api.post(`/api/v2/nodes/${disk.node_id}/disks/${disk.id}/blink_stop`);
    },
    RECOVER(_, { nodeId, diskId }) {
      return getClusterId()
        .then(clusterId => this.$api.post(`/api/v2/${clusterId}/nodes/${nodeId}/disks/${diskId}/recover`));
    },
    RUN_DIAGNOSTIC(_, { nodeId, diskId }) {
      return this.$api.get(`/api/v2/nodes/${nodeId}/disks/${diskId}/diagnostic-info/`)
        .then(res => res.data);
    },
    socket_disks({ commit }, { operation, object = {} }) {
      if (operation === 'created') {
        commit('ADD_DISK', object);
      } else if (operation === 'deleted') {
        commit('DELETE_DISK', object);
      } else if (operation === 'updated') {
        commit('PATCH_DISK', object);
      }
    }
  }
};
